import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { getProjects, generateFinancialsReport, Project } from "../services/api";

// Internal Amplify projects are excluded from every selection mode unless
// explicitly whitelisted — mirrors the standalone CLI report's behavior.
const EXCLUDED_PREFIXES = ["Amplify", "Fuse"];
const TM_BILLING_TYPES = ["based_on_staff_hours", "based_on_task_hours"];

type Mode = "projects" | "customers" | "managers" | "billing_type" | "all" | null;

const primaryBtnStyle: React.CSSProperties = {
    backgroundColor: "#F18E00", color: "white", border: "none", borderRadius: "4px",
    padding: "10px 24px", cursor: "pointer", fontSize: "14px", fontWeight: "bold",
};
const secondaryBtnStyle: React.CSSProperties = {
    backgroundColor: "white", color: "#5C5D5E", border: "1px solid #ddd",
    borderRadius: "4px", padding: "10px 24px", cursor: "pointer", fontSize: "14px",
};
const smallBtnStyle: React.CSSProperties = {
    backgroundColor: "white", color: "#5C5D5E", border: "1px solid #ddd",
    borderRadius: "4px", padding: "5px 12px", cursor: "pointer", fontSize: "12px",
};
const menuItemStyle: React.CSSProperties = {
    display: "flex", alignItems: "center", gap: "14px",
    padding: "16px 20px", border: "1px solid #e0e0e0", borderRadius: "6px",
    marginBottom: "10px", cursor: "pointer", backgroundColor: "white",
};
const menuNumberStyle: React.CSSProperties = {
    display: "flex", alignItems: "center", justifyContent: "center",
    width: "28px", height: "28px", borderRadius: "50%",
    backgroundColor: "#5C5D5E", color: "white", fontSize: "13px", fontWeight: "bold",
    flexShrink: 0,
};
const inputStyle: React.CSSProperties = {
    width: "100%", padding: "8px 10px", fontSize: "13px",
    border: "1px solid #ddd", borderRadius: "4px", boxSizing: "border-box",
};
const listBoxStyle: React.CSSProperties = {
    border: "1px solid #e0e0e0", borderRadius: "6px", maxHeight: "360px",
    overflowY: "auto", backgroundColor: "white",
};
const checkboxRowStyle: React.CSSProperties = {
    display: "flex", alignItems: "center", gap: "10px",
    padding: "9px 14px", fontSize: "13px", color: "#5C5D5E",
    borderBottom: "1px solid #f0f0f0", cursor: "pointer",
};

const Spinner: React.FC = () => (
    <>
        <style>{"@keyframes pf-spin { to { transform: rotate(360deg); } }"}</style>
        <div style={{
            width: "16px", height: "16px", borderRadius: "50%",
            border: "2px solid #ffffff88", borderTopColor: "#fff",
            animation: "pf-spin 0.8s linear infinite", display: "inline-block",
        }} />
    </>
);

const ProjectFinancials: React.FC = () => {
    const navigate = useNavigate();
    const [projects, setProjects] = useState<Project[]>([]);
    const [loadingProjects, setLoadingProjects] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);

    const [mode, setMode] = useState<Mode>(null);

    const [search, setSearch] = useState("");
    const [selectedProjectIds, setSelectedProjectIds] = useState<Set<number>>(new Set());
    const [selectedCustomers, setSelectedCustomers] = useState<Set<string>>(new Set());
    const [selectedManagers, setSelectedManagers] = useState<Set<string>>(new Set());
    const [billingChoice, setBillingChoice] = useState<"tm" | null>(null);

    const [generating, setGenerating] = useState(false);
    const [genError, setGenError] = useState<string | null>(null);

    const loadProjects = async () => {
        setLoadingProjects(true);
        setLoadError(null);
        try {
            const projs = await getProjects();
            setProjects(projs);
        } catch (e) {
            console.error("Failed to load projects", e);
            setLoadError("Failed to load projects. Please try again.");
        } finally {
            setLoadingProjects(false);
        }
    };

    useEffect(() => {
        loadProjects();
    }, []);

    const eligibleProjects = useMemo(() => {
        return projects.filter(p =>
            p.is_whitelisted || !EXCLUDED_PREFIXES.some(prefix => p.name.startsWith(prefix))
        );
    }, [projects]);

    const filteredProjects = useMemo(() => {
        const sorted = [...eligibleProjects].sort((a, b) => a.name.localeCompare(b.name));
        const term = search.trim().toLowerCase();
        if (!term) return sorted;
        return sorted.filter(p => p.name.toLowerCase().includes(term));
    }, [eligibleProjects, search]);

    const customerGroups = useMemo(() => {
        const counts: Record<string, number> = {};
        eligibleProjects.forEach(p => {
            const name = (p.customer || "").trim();
            if (!name) return;
            counts[name] = (counts[name] || 0) + 1;
        });
        return Object.entries(counts).sort((a, b) => a[0].localeCompare(b[0]));
    }, [eligibleProjects]);

    const managerGroups = useMemo(() => {
        const counts: Record<string, number> = {};
        eligibleProjects.forEach(p => {
            const name = (p.pm_name || "").trim();
            if (!name) return;
            counts[name] = (counts[name] || 0) + 1;
        });
        return Object.entries(counts).sort((a, b) => a[0].localeCompare(b[0]));
    }, [eligibleProjects]);

    const tmCount = useMemo(
        () => eligibleProjects.filter(p => TM_BILLING_TYPES.includes((p.billing_type || "").toLowerCase())).length,
        [eligibleProjects]
    );

    const resetSelections = () => {
        setSearch("");
        setSelectedProjectIds(new Set());
        setSelectedCustomers(new Set());
        setSelectedManagers(new Set());
        setBillingChoice(null);
        setGenError(null);
    };

    const backToMenu = () => {
        setMode(null);
        resetSelections();
    };

    const toggleProject = (id: number) => {
        setSelectedProjectIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const toggleCustomer = (name: string) => {
        setSelectedCustomers(prev => {
            const next = new Set(prev);
            if (next.has(name)) next.delete(name); else next.add(name);
            return next;
        });
    };

    const toggleManager = (name: string) => {
        setSelectedManagers(prev => {
            const next = new Set(prev);
            if (next.has(name)) next.delete(name); else next.add(name);
            return next;
        });
    };

    const canGenerate = (): boolean => {
        if (generating) return false;
        switch (mode) {
            case "projects":     return selectedProjectIds.size > 0;
            case "customers":    return selectedCustomers.size > 0;
            case "managers":     return selectedManagers.size > 0;
            case "billing_type": return billingChoice === "tm";
            case "all":          return true;
            default:             return false;
        }
    };

    const handleGenerate = async () => {
        if (!mode) return;
        setGenError(null);

        const payload: any = { mode };
        if (mode === "projects")  payload.selected_ids = Array.from(selectedProjectIds);
        if (mode === "customers") payload.selected_names = Array.from(selectedCustomers);
        if (mode === "managers")  payload.selected_names = Array.from(selectedManagers);
        if (mode === "billing_type") payload.billing_type = billingChoice;

        setGenerating(true);
        try {
            const blob = await generateFinancialsReport(payload);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            const ts = new Date();
            const pad = (n: number) => n.toString().padStart(2, "0");
            const stamp = `${ts.getFullYear()}${pad(ts.getMonth() + 1)}${pad(ts.getDate())}_${pad(ts.getHours())}${pad(ts.getMinutes())}${pad(ts.getSeconds())}`;
            a.href = url;
            a.download = `Project_Financials_${stamp}.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            navigate("/");
        } catch (e: any) {
            let message = "Failed to generate report. Please try again.";
            const data = e?.response?.data;
            if (data instanceof Blob) {
                try {
                    const text = await data.text();
                    const parsed = JSON.parse(text);
                    if (parsed?.detail) message = parsed.detail;
                } catch {
                    // couldn't parse the blob as JSON — fall back to the default message
                }
            } else if (e?.response?.data?.detail) {
                message = e.response.data.detail;
            }
            setGenError(message);
        } finally {
            setGenerating(false);
        }
    };

    const renderGenerateBar = () => (
        <div style={{ marginTop: "20px" }}>
            {genError && (
                <div style={{ color: "#C00000", fontSize: "13px", marginBottom: "10px" }}>{genError}</div>
            )}
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <button onClick={backToMenu} style={secondaryBtnStyle} disabled={generating}>
                    ← Back
                </button>
                <button
                    onClick={handleGenerate}
                    disabled={!canGenerate()}
                    style={{
                        ...primaryBtnStyle,
                        opacity: canGenerate() ? 1 : 0.5,
                        cursor: canGenerate() ? "pointer" : "not-allowed",
                        display: "flex", alignItems: "center", gap: "8px",
                    }}
                >
                    {generating && <Spinner />}
                    {generating ? "Generating report…" : "Generate Report"}
                </button>
                {generating && (
                    <span style={{ fontSize: "12px", color: "#666" }}>
                        This can take 10–20 seconds for larger selections.
                    </span>
                )}
            </div>
        </div>
    );

    if (loadingProjects) return <div>Loading projects...</div>;

    if (loadError) {
        return (
            <div>
                <div style={{ color: "#C00000", fontSize: "14px", marginBottom: "12px" }}>{loadError}</div>
                <button onClick={loadProjects} style={secondaryBtnStyle}>Retry</button>
            </div>
        );
    }

    return (
        <div>
            <h1 style={{ color: "#5C5D5E", margin: "0 0 6px 0", fontSize: "22px" }}>Project Financials Report</h1>
            <p style={{ color: "#666", marginTop: 0, marginBottom: "24px", fontSize: "13px" }}>
                Select how you'd like to choose projects, then generate the report.
            </p>

            {mode === null && (
                <div>
                    <div style={menuItemStyle} onClick={() => setMode("projects")}>
                        <span style={menuNumberStyle}>1</span>
                        <div>
                            <div style={{ fontWeight: "bold", fontSize: "14px", color: "#5C5D5E" }}>Select by Project</div>
                            <div style={{ fontSize: "12px", color: "#999" }}>Pick individual projects from a searchable list</div>
                        </div>
                    </div>
                    <div style={menuItemStyle} onClick={() => setMode("customers")}>
                        <span style={menuNumberStyle}>2</span>
                        <div>
                            <div style={{ fontWeight: "bold", fontSize: "14px", color: "#5C5D5E" }}>Select by Customer</div>
                            <div style={{ fontSize: "12px", color: "#999" }}>Include every project for one or more customers</div>
                        </div>
                    </div>
                    <div style={menuItemStyle} onClick={() => setMode("managers")}>
                        <span style={menuNumberStyle}>3</span>
                        <div>
                            <div style={{ fontWeight: "bold", fontSize: "14px", color: "#5C5D5E" }}>Select by Project Manager</div>
                            <div style={{ fontSize: "12px", color: "#999" }}>Include every project for one or more PMs</div>
                        </div>
                    </div>
                    <div style={menuItemStyle} onClick={() => { setMode("billing_type"); setBillingChoice("tm"); }}>
                        <span style={menuNumberStyle}>4</span>
                        <div>
                            <div style={{ fontWeight: "bold", fontSize: "14px", color: "#5C5D5E" }}>Select by Billing Type</div>
                            <div style={{ fontSize: "12px", color: "#999" }}>T&amp;M projects</div>
                        </div>
                    </div>
                    <div style={menuItemStyle} onClick={() => setMode("all")}>
                        <span style={menuNumberStyle}>5</span>
                        <div>
                            <div style={{ fontWeight: "bold", fontSize: "14px", color: "#5C5D5E" }}>All Active Projects</div>
                            <div style={{ fontSize: "12px", color: "#999" }}>Report on every active project ({eligibleProjects.length})</div>
                        </div>
                    </div>
                </div>
            )}

            {mode === "projects" && (
                <div>
                    <input
                        type="text"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        placeholder="Search projects..."
                        style={{ ...inputStyle, marginBottom: "10px" }}
                        disabled={generating}
                    />
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                        <span style={{ fontSize: "12px", color: "#666" }}>
                            {selectedProjectIds.size} of {eligibleProjects.length} selected
                        </span>
                        <div style={{ display: "flex", gap: "8px" }}>
                            <button
                                style={smallBtnStyle}
                                disabled={generating}
                                onClick={() => setSelectedProjectIds(new Set(filteredProjects.map(p => p.id)))}
                            >
                                Select All
                            </button>
                            <button
                                style={smallBtnStyle}
                                disabled={generating}
                                onClick={() => setSelectedProjectIds(new Set())}
                            >
                                Clear
                            </button>
                        </div>
                    </div>
                    <div style={listBoxStyle}>
                        {filteredProjects.length === 0 && (
                            <div style={{ padding: "14px", fontSize: "13px", color: "#999" }}>No projects match your search.</div>
                        )}
                        {filteredProjects.map(p => (
                            <label key={p.id} style={checkboxRowStyle}>
                                <input
                                    type="checkbox"
                                    checked={selectedProjectIds.has(p.id)}
                                    onChange={() => toggleProject(p.id)}
                                    disabled={generating}
                                />
                                <div>
                                    <div>{p.name}</div>
                                    {p.customer && <div style={{ fontSize: "11px", color: "#999" }}>{p.customer}</div>}
                                </div>
                            </label>
                        ))}
                    </div>
                    {renderGenerateBar()}
                </div>
            )}

            {mode === "customers" && (
                <div>
                    <div style={{ fontSize: "12px", color: "#666", marginBottom: "8px" }}>
                        {selectedCustomers.size} of {customerGroups.length} selected
                    </div>
                    <div style={listBoxStyle}>
                        {customerGroups.length === 0 && (
                            <div style={{ padding: "14px", fontSize: "13px", color: "#999" }}>No customers found.</div>
                        )}
                        {customerGroups.map(([name, count]) => (
                            <label key={name} style={checkboxRowStyle}>
                                <input
                                    type="checkbox"
                                    checked={selectedCustomers.has(name)}
                                    onChange={() => toggleCustomer(name)}
                                    disabled={generating}
                                />
                                <span>{name} ({count} project{count !== 1 ? "s" : ""})</span>
                            </label>
                        ))}
                    </div>
                    {renderGenerateBar()}
                </div>
            )}

            {mode === "managers" && (
                <div>
                    <div style={{ fontSize: "12px", color: "#666", marginBottom: "8px" }}>
                        {selectedManagers.size} of {managerGroups.length} selected
                    </div>
                    <div style={listBoxStyle}>
                        {managerGroups.length === 0 && (
                            <div style={{ padding: "14px", fontSize: "13px", color: "#999" }}>No project managers found.</div>
                        )}
                        {managerGroups.map(([name, count]) => (
                            <label key={name} style={checkboxRowStyle}>
                                <input
                                    type="checkbox"
                                    checked={selectedManagers.has(name)}
                                    onChange={() => toggleManager(name)}
                                    disabled={generating}
                                />
                                <span>{name} ({count} project{count !== 1 ? "s" : ""})</span>
                            </label>
                        ))}
                    </div>
                    {renderGenerateBar()}
                </div>
            )}

            {mode === "billing_type" && (
                <div>
                    <div
                        style={{
                            ...checkboxRowStyle, border: "1px solid #e0e0e0", borderRadius: "6px",
                            backgroundColor: "#FFF8EC",
                        }}
                    >
                        <input type="radio" checked readOnly />
                        <span>T&amp;M ({tmCount} project{tmCount !== 1 ? "s" : ""})</span>
                    </div>
                    {renderGenerateBar()}
                </div>
            )}

            {mode === "all" && (
                <div>
                    <div style={{ fontSize: "13px", color: "#5C5D5E", marginBottom: "8px" }}>
                        This will generate a report for all {eligibleProjects.length} active project{eligibleProjects.length !== 1 ? "s" : ""}.
                    </div>
                    {renderGenerateBar()}
                </div>
            )}
        </div>
    );
};

export default ProjectFinancials;
